import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

// Utils
import { useTranslation } from 'next-i18next';
import { isArray, isEqual, isNil } from 'lodash';
import { useRouter } from 'next/router';
import { useMediaQuery } from 'react-responsive';
import addOrRemoveFromArray from '../../../src/utils/addOrRemoveFromArray';
import { useAppContext } from '../../../src/store/AppContext';
import { useModal } from '../../../src/store/ModalContext';
import getLanguageId from '../../../src/utils/i18n/getLanguageId';
import useCart from '../../../src/hooks/useCart';
import cn from '../../../src/utils/cn';
import activePackageFilters from '../../../src/enum/activePackageFilters';
import useWeekId from '../../../src/hooks/useWeekId';

// Icons
import CalendarIcon from '../../../public/icons/calendar.svg';
import FilterIcon from '../../../public/icons/filter.svg';
import UserIcon from '../../../public/icons/user.svg';
import EditIcon from '../../../public/icons/edit.svg';

// Components
import HorizontalContainer from '../HorizontalContainer/HorizontalContainer';
import PeriodsList from '../Cart/PlayerEntry/PeriodsList/PeriodsList';
import Flag from '../../atoms/Flag/Flag';

import styles from './Filters.module.scss';
import MobileStepper from '../../layout/BookingTunnel/MobileStepper/MobileStepper';

function Filters({
  filters,
  setFilters,
  themes,
  moreFilters,
  isDisabled,
  weekSelectForm,
  isWeekendTheme,
}) {
  const { t } = useTranslation();
  const router = useRouter();
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const { getPlayerLabel } = useCart();
  const { getOfferDatesFromWeekId } = useWeekId();

  const [{ currentPlayer, search, players }, stateDispatch] = useAppContext();
  const { openModal } = useModal();

  useEffect(() => {
    if (search
      && Object.values(search).length
      && !isNil(weekSelectForm?.values?.weeks)
      && !weekSelectForm.values.weeks.length
      && weekSelectForm.values.weeks.length === 0
    ) {
      const searchWeeks = isArray(search.weeks) ? search.weeks : [search.weeks];
      weekSelectForm.setFieldValue('weeks', searchWeeks);
      weekSelectForm.setFieldValue('weekend', isWeekendTheme || search.weekend);
    }
  }, [search]);

  const handleThemeClick = (themeId) => {
    if (filters.themesIds) {
      const updated = addOrRemoveFromArray(filters.themesIds, themeId);
      setFilters((prev) => ({ ...prev, themesIds: updated }));
    }
  };

  useEffect(() => {
    // search from home by theme
    if (themes?.length && !isNil(router.query?.theme) && !isNil(currentPlayer?.typeId)) {
      const themesIdsByType = themes.filter((th) => th.typeId === currentPlayer.typeId).map((th) => th.id);
      const searchTheme = +router.query.theme;
      if (themesIdsByType.includes(searchTheme) && filters.themesIds?.length === 0) {
        handleThemeClick(+router.query.theme);
      }
    }
  }, [currentPlayer?.typeId]);

  useEffect(() => {
    if (weekSelectForm?.values) {
      const { weeks, weekend } = weekSelectForm.values;
      setFilters((prev) => ({
        ...prev,
        durationId: weekend ? 2 : 1,
        weekIds: weeks,
      }));

      if (search && weeks && !isEqual(search.weeks, weeks)) {
        stateDispatch({ type: 'SET_SEARCH', payload: { ...search, weeks } });
      }

      if (search && !isEqual(search.weekend, weekend)) {
        stateDispatch({ type: 'SET_SEARCH', payload: { ...search, weekend } });
      }
    }
  }, [weekSelectForm?.values]);

  const getUsedFiltersCount = useCallback(() => {
    const { sportIds, formatId, tagIds } = activePackageFilters;
    let count = 0;

    if (filters?.[sportIds]?.length > 0) count += filters[sportIds].length;
    if (filters?.[formatId] !== null) count += 1;
    if (filters?.[tagIds]?.length > 0) count += filters[tagIds].length;

    return count;
  }, [filters]);

  const selectedPeriods = useMemo(() => {
    if (!players?.length && !search) return [];

    const searchWeekIds = isArray(search?.weeks) ? search.weeks : (search?.weeks ? [search.weeks] : []);
    const weeks = players?.[0]?.stage?.weeks || searchWeekIds || [];

    const periods = weeks?.map((weekId) => (
      getOfferDatesFromWeekId({
        weekId,
        isWeekend: players?.[0]?.stage?.durationId === 2 || search.weekend,
        days: players?.[0]?.stage?.durationInDays || (search.weekend ? 2 : 5),
        withShortMonth: true,
      })
    )) || [];

    return periods;
  }, [players, search]);

  return (
    <section className={`${styles.filters} flex`}>
      <HorizontalContainer className="flex-auto">
        {themes?.length ? (
          themes.filter((th) => th.typeId === currentPlayer?.typeId).map((theme) => (
            <button
              type="button"
              key={theme.id}
              className={`${styles.theme} ${filters.themesIds.includes(theme.id) ? styles.active : ''}`}
              onClick={() => handleThemeClick(theme.id)}
            >
              <span className={styles.inner_theme}>{theme.name[getLanguageId(router.locale)]}</span>
            </button>
          ))
        ) : null}
      </HorizontalContainer>

      <div className={`${styles.cta_wrapper} flex a-center p-l-20`}>
        {isMobile && currentPlayer ? (
          <Flag noLower color="greyLight" className={`${styles.player_label} m-r-10`}>
            <div className="flex a-center">
              <UserIcon className="m-r-5" />
              {getPlayerLabel({
                typeLabel: currentPlayer.typeLabel,
                typeId: currentPlayer.typeId,
                playerId: currentPlayer.id,
              })}
            </div>
          </Flag>
        ) : null}
        {currentPlayer?.id === 1 ? (
          // only the first player can edit periods
          <button
            type="button"
            className={cn([
              styles.calendar,
              'flex a-center',
              selectedPeriods?.length ? '' : styles.no_period,
            ])}
            onClick={() => openModal('modal-calendar')}
          >
            <CalendarIcon className={cn([styles.prio_width, 'm-r-10'])} style={{ minWidth: 20 }} />

            {selectedPeriods?.length
            && selectedPeriods.some((period) => period !== '') ? (
              <>
                <span className={cn([styles.can_truncate, 'm-r-5'])}>{selectedPeriods[0]}</span>
                <span className={cn([styles.prio_width, 'text-c-secondary'])}>
                  {selectedPeriods.length > 1 ? ` +${selectedPeriods.length - 1}` : null}
                </span>
              </>
              ) : (
                <span className={styles.can_truncate}>
                  {t('common.noPeriod')}
                </span>
              )}

            <span className={cn([styles.prio_width, 'flex center text-c-secondary'])}>
              <EditIcon className="m-l-10" style={{ minWidth: 20 }} />
            </span>
          </button>
        ) : (
          players?.length && players[0]?.stage ? (
            <div
              className={cn([
                styles.periods_readonly,
                'flex a-center m-r-15 m-l-10',
              ])}
            >
              <CalendarIcon className={cn([styles.prio_width, 'm-r-10 text-c-black-700'])} style={{ minWidth: 20 }} />
              <PeriodsList
                className={cn([styles.periods_readonly_list, 'fw-500 text-c-grey-700'])}
                listLength={1}
                offersIds={players[0]?.stage.weeks}
                durationId={players[0]?.stage.durationId}
                days={players[0]?.stage.durationInDays}
                withBackground
              />
            </div>
          ) : null
        )}
        {Object.values(moreFilters).length ? (
          <button
            type="button"
            className={`${styles.more_filters} flex a-center m-l-auto`}
            onClick={() => (!isDisabled ? openModal('modal-more-filters') : () => {})}
            disabled={isDisabled}
          >
            <div className={cn([styles.filter_icon, 'flex center p-relative m-r-10'])}>
              <FilterIcon style={{ minWidth: 16 }} width={16} height={16} />

              <span
                className={cn([
                  'fs-10',
                  styles.badge_count,
                  getUsedFiltersCount() > 0 ? styles.show : '',
                ])}
              >
                {getUsedFiltersCount() || 1}
              </span>
            </div>
            {!isMobile ? (
              <span className="text-uppercase">{t('common.filters')}</span>
            ) : null}

          </button>
        ) : null}

        {isMobile ? (
          <div className={styles.stepper}>
            <MobileStepper />
          </div>
        ) : null}
      </div>
    </section>
  );
}

Filters.propTypes = {
  filters: PropTypes.shape({}),
  setFilters: PropTypes.func,
  themes: PropTypes.arrayOf(PropTypes.shape({})),
  moreFilters: PropTypes.shape({}),
  isDisabled: PropTypes.bool,
  weekSelectForm: PropTypes.shape({
    values: PropTypes.shape({}),
    setFieldValue: PropTypes.func,
  }),
  isWeekendTheme: PropTypes.bool,
};

Filters.defaultProps = {
  filters: {},
  setFilters: () => {},
  themes: [],
  moreFilters: {},
  isDisabled: false,
  weekSelectForm: {
    values: {},
    setFieldValue: () => {},
  },
  isWeekendTheme: false,
};

export default Filters;
